import { RoleType } from '@/core/admin/constants/role.constants';

/**
 * Role / WM-team gating shared by the sidebar menus (`app.menu.ts`), the
 * app registry (`app-registry.ts`) and the admin registry
 * (`admin-registry.ts`), so the masthead tabs and the sidebar rows agree on
 * who sees what. Extracted from `AppMenu.filterMenuItems` and
 * `AppMenu.hasRequiredRoles` in SCRUM-589 without changing behaviour.
 *
 * Rules (first match wins):
 * - `requiresWmEnabledTeam` hides the item unless the user has a WM-enabled
 *   team; System and Organization admins are exempt from that gate.
 * - No `requiredRoles` (or an empty list) means visible to everyone.
 * - SYSTEM_ADMIN sees everything.
 * - Otherwise the user needs ANY of `requiredRoles`, or ALL of them when
 *   `requireAllRoles` is set.
 *
 * Dropping a parent whose children are all hidden is a tree concern and stays
 * with the caller (see `AppMenu.filterMenuItems`).
 */

/** The gating fields an item (menu entry, app, section) may carry. */
export interface MenuGate {
  /** Roles required to see this item (ANY of these unless `requireAllRoles`). */
  requiredRoles?: readonly string[];
  /** If true, the user must have ALL `requiredRoles`. */
  requireAllRoles?: boolean;
  /** Data gate: needs a WM-enabled team (System/Org admins exempt). */
  requiresWmEnabledTeam?: boolean;
}

export interface MenuVisibilityContext {
  /** The current user's role types (`RoleType` values). */
  roles: readonly string[];
  /** Whether at least one of the user's teams is Workspace-Management-enabled. */
  hasWmEnabledTeam: boolean;
}

export function isMenuItemVisible(item: MenuGate, ctx: MenuVisibilityContext): boolean {
  const isSystemAdmin = ctx.roles.includes(RoleType.SYSTEM_ADMIN);
  const isOrganizationAdmin = ctx.roles.includes(RoleType.ORGANIZATION_ADMIN);

  // Data gate: WM items need a WM-enabled team (admins exempt)
  if (item.requiresWmEnabledTeam && !isSystemAdmin && !isOrganizationAdmin && !ctx.hasWmEnabledTeam) {
    return false;
  }

  return hasRequiredRoles(item, ctx.roles);
}

export function hasRequiredRoles(item: MenuGate, roles: readonly string[]): boolean {
  // If no role requirements, item is visible to all
  if (!item.requiredRoles || item.requiredRoles.length === 0) {
    return true;
  }

  // System admin has root access - can see everything
  if (roles.includes(RoleType.SYSTEM_ADMIN)) {
    return true;
  }

  if (item.requireAllRoles) {
    // User must have ALL specified roles
    return item.requiredRoles.every((role) => roles.includes(role));
  }
  // User needs ANY of the specified roles
  return item.requiredRoles.some((role) => roles.includes(role));
}
